import * as Schema from "effect/Schema";

import { IsoDateTime, NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas.ts";

/**
 * Which commits the Contributions tab reads: the checkout's whole branch, or
 * only the commits made since the thread began.
 */
export const ContributionsRange = Schema.Literals(["branch", "thread"]);
export type ContributionsRange = typeof ContributionsRange.Type;

export const CONTRIBUTIONS_PAGE_SIZE = 50;

const ContributionsScope = {
  /** The thread's checkout: its Primary Checkout or Thread Worktree. */
  cwd: TrimmedNonEmptyString,
  range: ContributionsRange,
  /** When the thread began; required for the `thread` range. */
  since: Schema.optional(IsoDateTime),
};

export const ContributionsQueryInput = Schema.Struct({
  ...ContributionsScope,
  /** Commits to skip, from the previous page's `nextCursor`. */
  cursor: Schema.optional(NonNegativeInt),
});
export type ContributionsQueryInput = typeof ContributionsQueryInput.Type;

export const ContributionsModelLines = Schema.Struct({
  tool: Schema.String,
  model: Schema.String,
  aiLines: NonNegativeInt,
});
export type ContributionsModelLines = typeof ContributionsModelLines.Type;

const AuthorshipLines = {
  aiLines: NonNegativeInt,
  humanLines: NonNegativeInt,
  /** Added lines no Authorship Log attests: credited to nobody. */
  unknownLines: NonNegativeInt,
};

export const ContributionsSummary = Schema.Struct({
  ...AuthorshipLines,
  byModel: Schema.Array(ContributionsModelLines),
  commitCount: NonNegativeInt,
  commitsWithAuthorshipLog: NonNegativeInt,
  /** Set when the range had more commits than are counted: only the newest `commitCount` are. */
  newestOnly: Schema.Boolean,
});
export type ContributionsSummary = typeof ContributionsSummary.Type;

export const ContributionsCommit = Schema.Struct({
  ...AuthorshipLines,
  sha: TrimmedNonEmptyString,
  subject: Schema.String,
  committedAt: IsoDateTime,
  hasAuthorshipLog: Schema.Boolean,
});
export type ContributionsCommit = typeof ContributionsCommit.Type;

/**
 * Whether this Environment is recording Authorship Logs right now. Existing
 * logs stay readable either way; `unavailable` means new work is not tracked.
 */
export const AuthorshipTracking = Schema.Literals(["active", "unavailable"]);
export type AuthorshipTracking = typeof AuthorshipTracking.Type;

export const ContributionsQueryResult = Schema.Struct({
  summary: ContributionsSummary,
  commits: Schema.Array(ContributionsCommit),
  nextCursor: Schema.NullOr(NonNegativeInt),
  tracking: AuthorshipTracking,
});
export type ContributionsQueryResult = typeof ContributionsQueryResult.Type;

export const ContributionsExportInput = Schema.Struct(ContributionsScope);
export type ContributionsExportInput = typeof ContributionsExportInput.Type;

export const ContributionsExportResult = Schema.Struct({
  fileName: TrimmedNonEmptyString,
  /** Agent Trace records, one JSON object per line. */
  content: Schema.String,
  recordCount: NonNegativeInt,
});
export type ContributionsExportResult = typeof ContributionsExportResult.Type;

export const ContributionsFailure = Schema.Literals(["not_a_repository", "operation_failed"]);
export type ContributionsFailure = typeof ContributionsFailure.Type;

export class ContributionsError extends Schema.TaggedErrorClass<ContributionsError>()(
  "ContributionsError",
  {
    cwd: TrimmedNonEmptyString,
    failure: ContributionsFailure,
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

/** The Agent Trace 0.1 export format; it is never persisted as AFAR state. */
export const AgentTraceContributor = Schema.Struct({
  type: Schema.Literals(["human", "ai", "mixed", "unknown"]),
  model_id: Schema.optional(Schema.String.check(Schema.isMaxLength(250))),
});
export type AgentTraceContributor = typeof AgentTraceContributor.Type;

export const AgentTraceRange = Schema.Struct({
  start_line: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  end_line: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
});
export type AgentTraceRange = typeof AgentTraceRange.Type;

export const AgentTraceConversation = Schema.Struct({
  contributor: AgentTraceContributor,
  ranges: Schema.Array(AgentTraceRange),
});
export type AgentTraceConversation = typeof AgentTraceConversation.Type;

export const AgentTraceRecord = Schema.Struct({
  version: Schema.String.check(Schema.isPattern(/^[0-9]+\.[0-9]+$/)),
  id: Schema.String.check(
    Schema.isPattern(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i),
  ),
  timestamp: IsoDateTime,
  vcs: Schema.Struct({ type: Schema.Literal("git"), revision: TrimmedNonEmptyString }),
  tool: Schema.Struct({ name: Schema.String, version: Schema.String }),
  files: Schema.Array(
    Schema.Struct({
      path: Schema.String,
      conversations: Schema.Array(AgentTraceConversation),
    }),
  ),
  metadata: Schema.Record(Schema.String, Schema.Unknown),
});
export type AgentTraceRecord = typeof AgentTraceRecord.Type;
